import React from "react";
import {
  Car,
  Calendar,
  FuelIcon as Engine,
  Tag,
  ImageIcon,
  Loader2,
  AlertCircle,
  Lock,
  Download,
  CheckCircle,
  AlertTriangle,
  Gavel,
  ClipboardList,
} from "lucide-react";
import useGetCarReportPreview from "../../../../hooks/report/useGetReportPreview";
import { useAuthContext } from "../../../../hooks/useAuthContext";
import { useRef, useCallback } from "react";
import { Link } from "react-router-dom";

const CarPreview = ({ vin, onGetReport }) => {
  const {
    reportPreview,
    loading: reportPreviewFetchLoading,
    error: reportPreviewError,
    fetchCarReportPreview,
  } = useGetCarReportPreview(vin);

  const { user } = useAuthContext();
  const signInDialogRef = useRef(null);

  const handleGetReport = () => {
    if (!user) {
      const dialog = signInDialogRef.current || document.getElementById("report_sign_in_modal");
      if (!dialog) return;
      try {
        if (!dialog.open) dialog.showModal();
      } catch (e) {
        dialog.show?.();
      }
      return;
    }
    onGetReport();
  };

  const cleanupBodyScroll = useCallback(() => {
    document.body.classList.remove("modal-open");
    document.documentElement.classList.remove("modal-open");
    document.body.style.overflow = "";
    document.documentElement.style.overflow = "";
  }, []);

  const handleCloseModal = useCallback(() => {
    const dialog = signInDialogRef.current || document.getElementById("report_sign_in_modal");
    try {
      if (dialog?.open) dialog.close();
    } finally {
      cleanupBodyScroll();
    }
  }, [cleanupBodyScroll]);

  React.useEffect(() => {
    const dialog = signInDialogRef.current || document.getElementById("report_sign_in_modal");
    if (!dialog) return;

    const onClose = () => cleanupBodyScroll();
    const onCancel = (e) => {
      e.preventDefault();
      handleCloseModal();
    };

    dialog.addEventListener("close", onClose);
    dialog.addEventListener("cancel", onCancel);
    return () => {
      dialog.removeEventListener("close", onClose);
      dialog.removeEventListener("cancel", onCancel);
      cleanupBodyScroll();
      try {
        if (dialog.open) dialog.close();
      } catch {}
    };
  }, [handleCloseModal, cleanupBodyScroll]);

  const {
    previewImageURL,
    imagesAmount,
    recalls,
    auctionHistoryRecords,
    vinSpec,
  } = reportPreview?.data?.result || {};

  const fetchPreviewRef = React.useRef(fetchCarReportPreview);
  React.useEffect(() => {
    fetchPreviewRef.current = fetchCarReportPreview;
  }, [fetchCarReportPreview]);

  React.useEffect(() => {
    if (!vin) return;
    fetchPreviewRef.current();
  }, [vin]);

  return (
    <div className="w-full mx-auto p-[4vw] md:p-[3vw] lg:p-[2vw] bg-white shadow-md rounded-[1vw] border border-gray-200">
      {/* Loading State */}
      {reportPreviewFetchLoading && (
        <div className="flex flex-col items-center justify-center py-[5vw]">
          <Loader2 className="w-[8vw] h-[8vw] md:w-[5vw] md:h-[5vw] lg:w-[3vw] lg:h-[3vw] text-red-600 animate-spin mb-[1vw]" />
          <p className="text-[4vw] md:text-[2.5vw] lg:text-[1.2vw] text-gray-600 font-medium">
            Loading report preview...
          </p>
        </div>
      )}

      {/* Error State */}
      {reportPreviewError && (
        <div className="flex flex-col items-center justify-center py-[5vw]">
          <AlertCircle className="w-[8vw] h-[8vw] md:w-[5vw] md:h-[5vw] lg:w-[3vw] lg:h-[3vw] text-red-600 mb-[1vw]" />
          <p className="text-[4vw] md:text-[2.5vw] lg:text-[1.2vw] text-gray-700 font-medium">
            No Report Found.
          </p>
        </div>
      )}

      {/* Report Content */}
      {reportPreview && (
        <div className="w-full h-full">
          {/* Header Section */}
          <div className="mb-[2vw] md:mb-[1.5vw] lg:mb-[1vw] flex items-center">
            <CheckCircle className="w-[6vw] h-[6vw] md:w-[3.5vw] md:h-[3.5vw] lg:w-[1.8vw] lg:h-[1.8vw] text-green-600 mr-[0.8vw]" />
            <h2 className="text-[8vw] md:text-[2.0vw] lg:text-[1.5vw] font-bold text-gray-800">
              Report Found!
            </h2>
          </div>

          {/* Car Image Preview Section */}
          <div className="mb-[2vw] md:mb-[1.5vw] lg:mb-[1vw]">
            <div className="relative rounded-[0.5vw] overflow-hidden border border-gray-200">
              {previewImageURL ? (
                <>
                  <img
                    src={previewImageURL || "/placeholder.svg"}
                    alt="Car Preview"
                    className="w-full h-[30vw] md:h-[20vw] lg:h-[12vw] object-cover blur-[4px] filter brightness-90"
                  />
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/30">
                    <Lock className="w-[6vw] h-[6vw] md:w-[4vw] md:h-[4vw] lg:w-[2vw] lg:h-[2vw] text-white mb-[1vw]" />
                    <p className="text-white text-[2.5vw] md:text-[1.5vw] lg:text-[0.8vw] font-semibold">
                      Get Full Report to Unlock
                    </p>
                  </div>
                  <div className="absolute bottom-[1vw] right-[1vw] bg-black/70 text-white px-[1vw] py-[0.5vw] rounded-full flex items-center">
                    <ImageIcon className="w-[2vw] h-[2vw] md:w-[1.2vw] md:h-[1.2vw] lg:w-[0.6vw] lg:h-[0.6vw] mr-[0.3vw]" />
                    <span className="text-[2vw] md:text-[1.2vw] lg:text-[0.6vw]">
                      {imagesAmount} Images
                    </span>
                  </div>
                </>
              ) : (
                <div className="w-full h-[30vw] md:h-[20vw] lg:h-[12vw] bg-gray-100 flex items-center justify-center">
                  <ImageIcon className="w-[6vw] h-[6vw] md:w-[4vw] md:h-[4vw] lg:w-[2vw] lg:h-[2vw] text-gray-400" />
                </div>
              )}
            </div>
          </div>

          <div className="border border-gray-200 rounded-[1vw] overflow-hidden mb-[4vw] md:mb-[3vw] lg:mb-[2vw]">
            {/* Vehicle Information Section */}
            <div>
              <div className="flex items-center p-[2vw] md:p-[1.5vw] lg:p-[0.8vw] border-b border-gray-200 bg-gray-50">
                <ClipboardList className="w-[4vw] h-[4vw] md:w-[2.5vw] md:h-[2.5vw] lg:w-[1.2vw] lg:h-[1.2vw] text-gray-700 mr-[0.8vw]" />
                <h2 className="text-[3.5vw] md:text-[2.5vw] lg:text-[1.2vw] font-semibold text-gray-800">
                  Vehicle Information
                </h2>
              </div>

              <div className="bg-white">
                <div className="grid grid-cols-2 gap-x-[2vw] md:gap-x-[1.5vw] lg:gap-x-[0.8vw]">
                  {/* VIN */}
                  <div className="col-span-2 p-[2vw] md:p-[1.5vw] lg:p-[0.8vw] border-b border-gray-200 flex items-center">
                    <div className="w-[6vw] md:w-[4vw] lg:w-[2.5vw] h-[6vw] md:h-[4vw] lg:h-[2.5vw] bg-gray-100 rounded-full flex items-center justify-center mr-[1.5vw] md:mr-[1vw] lg:mr-[0.8vw]">
                      <Tag className="w-[3vw] md:w-[2vw] lg:w-[1vw] h-[3vw] md:h-[2vw] lg:h-[1vw] text-gray-700" />
                    </div>
                    <div className="flex-1">
                      <p className="text-[2.2vw] md:text-[1.4vw] lg:text-[0.8vw] font-medium text-gray-500">
                        VIN:
                      </p>
                      <p className="text-[2.9vw] md:text-[1.9vw] lg:text-[1.1vw] text-gray-800 font-bold tracking-wide">
                        {vinSpec?.vin || "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* Make */}
                  <div className="p-[2vw] md:p-[1.5vw] lg:p-[0.8vw] border-b border-gray-200 flex items-center">
                    <div className="w-[6vw] md:w-[4vw] lg:w-[2.5vw] h-[6vw] md:h-[4vw] lg:h-[2.5vw] bg-gray-100 rounded-full flex items-center justify-center mr-[1.5vw] md:mr-[1vw] lg:mr-[0.8vw]">
                      <Car className="w-[3vw] md:w-[2vw] lg:w-[1vw] h-[3vw] md:h-[2vw] lg:h-[1vw] text-gray-700" />
                    </div>
                    <div className="flex-1">
                      <p className="text-[2.2vw] md:text-[1.4vw] lg:text-[0.8vw] font-medium text-gray-500">
                        Make:
                      </p>
                      <p className="text-[2.9vw] md:text-[1.9vw] lg:text-[1.1vw] text-gray-800 font-bold">
                        {vinSpec?.make || "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* Model */}
                  <div className="p-[2vw] md:p-[1.5vw] lg:p-[0.8vw] border-b border-gray-200 flex items-center">
                    <div className="w-[6vw] md:w-[4vw] lg:w-[2.5vw] h-[6vw] md:h-[4vw] lg:h-[2.5vw] bg-gray-100 rounded-full flex items-center justify-center mr-[1.5vw] md:mr-[1vw] lg:mr-[0.8vw]">
                      <Car className="w-[3vw] md:w-[2vw] lg:w-[1vw] h-[3vw] md:h-[2vw] lg:h-[1vw] text-gray-700" />
                    </div>
                    <div className="flex-1">
                      <p className="text-[2.2vw] md:text-[1.4vw] lg:text-[0.8vw] font-medium text-gray-500">
                        Model:
                      </p>
                      <p className="text-[2.9vw] md:text-[1.9vw] lg:text-[1.1vw] text-gray-800 font-bold">
                        {vinSpec?.model || "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* Year */}
                  <div className="p-[2vw] md:p-[1.5vw] lg:p-[0.8vw] border-b border-gray-200 flex items-center">
                    <div className="w-[6vw] md:w-[4vw] lg:w-[2.5vw] h-[6vw] md:h-[4vw] lg:h-[2.5vw] bg-gray-100 rounded-full flex items-center justify-center mr-[1.5vw] md:mr-[1vw] lg:mr-[0.8vw]">
                      <Calendar className="w-[3vw] md:w-[2vw] lg:w-[1vw] h-[3vw] md:h-[2vw] lg:h-[1vw] text-gray-700" />
                    </div>
                    <div className="flex-1">
                      <p className="text-[2.2vw] md:text-[1.4vw] lg:text-[0.8vw] font-medium text-gray-500">
                        Year:
                      </p>
                      <p className="text-[2.9vw] md:text-[1.9vw] lg:text-[1.1vw] text-gray-800 font-bold">
                        {vinSpec?.year || "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* Engine */}
                  <div className="p-[2vw] md:p-[1.5vw] lg:p-[0.8vw] border-b border-gray-200 flex items-center">
                    <div className="w-[6vw] md:w-[4vw] lg:w-[2.5vw] h-[6vw] md:h-[4vw] lg:h-[2.5vw] bg-gray-100 rounded-full flex items-center justify-center mr-[1.5vw] md:mr-[1vw] lg:mr-[0.8vw]">
                      <Engine className="w-[3vw] md:w-[2vw] lg:w-[1vw] h-[3vw] md:h-[2vw] lg:h-[1vw] text-gray-700" />
                    </div>
                    <div className="flex-1">
                      <p className="text-[2.2vw] md:text-[1.4vw] lg:text-[0.8vw] font-medium text-gray-500">
                        Engine:
                      </p>
                      <p className="text-[2.9vw] md:text-[1.9vw] lg:text-[1.1vw] text-gray-800 font-bold">
                        {vinSpec?.engine || "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* Trim */}
                  <div className="p-[2vw] md:p-[1.5vw] lg:p-[0.8vw] border-b border-gray-200 flex items-center">
                    <div className="w-[6vw] md:w-[4vw] lg:w-[2.5vw] h-[6vw] md:h-[4vw] lg:h-[2.5vw] bg-gray-100 rounded-full flex items-center justify-center mr-[1.5vw] md:mr-[1vw] lg:mr-[0.8vw]">
                      <Tag className="w-[3vw] md:w-[2vw] lg:w-[1vw] h-[3vw] md:h-[2vw] lg:h-[1vw] text-gray-700" />
                    </div>
                    <div className="flex-1">
                      <p className="text-[2.2vw] md:text-[1.4vw] lg:text-[0.8vw] font-medium text-gray-500">
                        Trim:
                      </p>
                      <p className="text-[2.9vw] md:text-[1.9vw] lg:text-[1.1vw] text-gray-800 font-bold">
                        {vinSpec?.trim || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recalls Section */}
            {recalls && (
              <div>
                <div className="flex items-center p-[2vw] md:p-[1.5vw] lg:p-[0.8vw] border-b border-gray-200 bg-gray-50">
                  <AlertTriangle className="w-[4vw] h-[4vw] md:w-[2.5vw] md:h-[2.5vw] lg:w-[1.2vw] lg:h-[1.2vw] text-amber-500 mr-[0.8vw]" />
                  <h2 className="text-[3.5vw] md:text-[2.5vw] lg:text-[1.2vw] font-semibold text-gray-800">
                    Recalls
                  </h2>
                </div>

                <div className="p-[2vw] md:p-[1.5vw] lg:p-[0.8vw] border-b border-gray-200">
                  <div className="flex items-center">
                    <div className="w-[7vw] md:w-[5vw] lg:w-[2.5vw] h-[7vw] md:h-[5vw] lg:h-[2.5vw] bg-red-100 rounded-full flex items-center justify-center mr-[1vw] md:mr-[0.8vw] lg:mr-[0.5vw]">
                      <span className="text-[3vw] md:text-[2vw] lg:text-[1vw] font-bold text-red-600">
                        {recalls.length}
                      </span>
                    </div>
                    <div>
                      <p className="text-[2.2vw] md:text-[1.3vw] lg:text-[0.8vw] font-medium text-gray-500">
                        Recalls:
                      </p>
                      <p className="text-[2.9vw] md:text-[1.9vw] lg:text-[1.1vw] font-semibold text-gray-800">
                        {recalls.length}{" "}
                        {recalls.length === 1 ? "Recall" : "Recalls"} Found
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Auction History */}
            {/* Auction History */}
            <div>
              <div className="flex items-center p-[2vw] md:p-[1.5vw] lg:p-[0.8vw] border-b border-gray-200 bg-gray-50">
                <Gavel className="w-[4vw] h-[4vw] md:w-[2.5vw] md:h-[2.5vw] lg:w-[1.2vw] lg:h-[1.2vw] text-gray-700 mr-[0.8vw]" />
                <h2 className="text-[3.5vw] md:text-[2.5vw] lg:text-[1.2vw] font-semibold text-gray-800">
                  Auction History
                </h2>
              </div>

              <div className="p-[2vw] md:p-[1.5vw] lg:p-[0.8vw]">
                {auctionHistoryRecords > 0 ? (
                  <div className="flex items-center">
                    <div className="w-[7vw] md:w-[5vw] lg:w-[2.5vw] h-[7vw] md:h-[5vw] lg:h-[2.5vw] bg-green-100 rounded-full flex items-center justify-center mr-[1vw] md:mr-[0.8vw] lg:mr-[0.5vw]">
                      <span className="text-[3vw] md:text-[2vw] lg:text-[1vw] font-bold text-green-600">
                        {auctionHistoryRecords}
                      </span>
                    </div>
                    <div>
                      <p className="text-[2vw] md:text-[1.2vw] lg:text-[0.7vw] font-medium text-gray-500">
                        Auction Records:
                      </p>
                      <p className="text-[2.9vw] md:text-[1.9vw] lg:text-[1.1vw] font-semibold text-black">
                        {auctionHistoryRecords}{" "}
                        {auctionHistoryRecords === 1 ? "Record" : "Records"}{" "}
                        Found
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <div className="w-[7vw] md:w-[5vw] lg:w-[2.5vw] h-[7vw] md:h-[5vw] lg:h-[2.5vw] bg-red-100 rounded-full flex items-center justify-center mr-[1vw] md:mr-[0.8vw] lg:mr-[0.5vw]">
                      <AlertCircle className="w-[3vw] h-[3vw] md:w-[2vw] md:h-[2vw] lg:w-[1vw] lg:h-[1vw] text-red-600" />
                    </div>
                    <div>
                      <p className="text-[2.2vw] md:text-[1.3vw] lg:text-[0.8vw] font-medium text-gray-500">
                        Auction Records:
                      </p>
                      <p className="text-[2.5vw] md:text-[1.5vw] lg:text-[0.9vw] text-red-600">
                        No auction history available
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Get Full Report Button */}
          <div className="mt-[3vw] md:mt-[2vw] lg:mt-[1vw]">
            <button
              onClick={handleGetReport}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-[2vw] md:py-[1.4vw] lg:py-[0.8vw] rounded-[0.5vw] text-[2.8vw] md:text-[1.8vw] lg:text-[1vw] shadow-md flex items-center justify-center"
            >
              <Download className="w-[2.6vw] h-[2.6vw] md:w-[1.6vw] md:h-[1.6vw] lg:w-[0.9vw] lg:h-[0.9vw] mr-[0.8vw]" />
              Buy Full Report
            </button>
          </div>
        </div>
      )}

      {/* Custom Sign In Modal for Report */}
      <dialog
        id="report_sign_in_modal"
        ref={signInDialogRef}
        className="modal"
      >
        <div className="modal-box dark:bg-white max-h-[90vh] overflow-y-auto sm:max-w-[95vw]">
          <h3 className="font-bold text-lg">Sign In Required</h3>
          <p className="py-4">Please sign in to get full report.</p>
          <div className="flex gap-x-4 justify-center items-center">
            <button
              className="btn text-red-600 dark:bg-white"
              onClick={handleCloseModal}
            >
              Close
            </button>
            <Link to="/login">
              <button className="btn text-green-600 dark:bg-white">
                Sign In
              </button>
            </Link>
          </div>
        </div>
      </dialog>
    </div>
  );
};

export default CarPreview;
